// Considered: no existing rating-summary + review-card combination atom/molecule.
// Uses ChipAtom for star display is rejected — ChipAtom is for text chips, not star icons.
// See spec Reuse Audit.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import {
  getChannelReviews,
  type ChannelReview,
} from '../../../api/marketplaceApi';
import './ChannelReviewsSectionOrganism.scss';

export interface ChannelReviewsSectionOrganismProps {
  channelId: string;
}

function StarRow({ rating, small = false }: Readonly<{ rating: number; small?: boolean }>) {
  const { t } = useTranslation();
  const size = small ? 'channel-reviews__star--small' : '';
  return (
    <span
      className="channel-reviews__stars"
      aria-label={t('marketplace.channelManage.v3Reviews.starsAriaLabel', { rating: Math.round(rating) })}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          className={`pi ${n <= Math.round(rating) ? 'pi-star-fill' : 'pi-star'} channel-reviews__star ${size}`}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

function RatingSummary({ reviews }: Readonly<{ reviews: ChannelReview[] }>) {
  const { t } = useTranslation();
  const avg =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0;

  return (
    <div className="channel-reviews__summary">
      <div className="channel-reviews__avg-block">
        <span className="channel-reviews__avg">{avg.toFixed(1)}</span>
        <StarRow rating={avg} />
        <span className="channel-reviews__count">
          {t('marketplace.channelManage.v3Reviews.reviewCount', { count: reviews.length })}
        </span>
      </div>

      <div className="channel-reviews__breakdown">
        {[5, 4, 3, 2, 1].map((n) => {
          const count = reviews.filter((r) => r.rating === n).length;
          const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
          return (
            <div key={n} className="channel-reviews__bar-row">
              <span className="channel-reviews__bar-label">{n}★</span>
              <div className="channel-reviews__bar-track">
                <div
                  className="channel-reviews__bar-fill"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="channel-reviews__bar-count">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewCard({
  review,
}: Readonly<{
  review: ChannelReview;
}>) {
  const { t } = useTranslation();

  const { author } = review;

  return (
    <div className="channel-reviews__card">
      <div className="channel-reviews__card-header">
        {author ? (
          <SphereIdentityAtom
            sphereId={author.subjectSphereId ?? undefined}
            sphereName={author.displayName}
            glyph={author.glyph ?? undefined}
            glyphColor={author.glyphColor ?? undefined}
            imageId={author.imageId ?? undefined}
            size="small"
            className="channel-reviews__card-author"
          />
        ) : (
          <span className="channel-reviews__card-author channel-reviews__card-author--anonymous">
            {t('marketplace.channelManage.v3Reviews.anonymousAuthor')}
          </span>
        )}
        <StarRow rating={review.rating} small />
        <div className="channel-reviews__card-actions">
          <button
            type="button"
            className="channel-reviews__action-btn"
            aria-label={t('marketplace.channelManage.v3Reviews.reply')}
          >
            <i className="pi pi-reply" aria-hidden="true" />
            {t('marketplace.channelManage.v3Reviews.reply')}
          </button>
          <button
            type="button"
            className="channel-reviews__action-btn channel-reviews__action-btn--flag"
            aria-label={t('marketplace.channelManage.v3Reviews.flag')}
          >
            <i className="pi pi-flag" aria-hidden="true" />
          </button>
        </div>
      </div>
      <p className="channel-reviews__card-text">{review.text}</p>
    </div>
  );
}

const ChannelReviewsSectionOrganism: React.FC<ChannelReviewsSectionOrganismProps> = ({
  channelId,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const [reviews, setReviews] = useState<ChannelReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!apiClients) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getChannelReviews(apiClients.schemaClient, channelId)
      .then((res) => setReviews(res.reviews))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, [apiClients, channelId]);

  return (
    <div className="channel-reviews">
      <div className="channel-reviews__section-card">
        <div className="channel-reviews__section-header">
          <h2 className="channel-reviews__section-title">
            {t('marketplace.channelManage.v3Reviews.title')}
          </h2>
          <p className="channel-reviews__section-subtitle">
            {t('marketplace.channelManage.v3Reviews.subtitle')}
          </p>
        </div>

        {loading ? (
          <div className="channel-reviews__loading">
            <span className="pi pi-spin pi-spinner" aria-hidden="true" />
          </div>
        ) : (
          <>
            <RatingSummary reviews={reviews} />

            {reviews.length === 0 ? (
              <p className="channel-reviews__empty">
                {t('marketplace.channelManage.v3Reviews.empty')}
              </p>
            ) : (
              <div className="channel-reviews__list">
                {reviews.map((r) => (
                  <ReviewCard key={r.id} review={r} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ChannelReviewsSectionOrganism.displayName = 'ChannelReviewsSectionOrganism';

export default ChannelReviewsSectionOrganism;
